import React from 'react';
import { BookOpen, Users, Zap, ArrowRight } from 'lucide-react';
import HomePageBackground from './images/Home-Page-Background.png'
import Navbar from './components/Navbar';
import HomeAbout from './components/Home-About';
import Footer from './components/Footer';
import AITrainerKitSection from './components/AI-Trainer';
import AIRevolutionCTA from './components/Community-Partnership';
import AIFoundationLayout from './components/About-More-Detail-Home';
import HowWeWorkSection from './components/How-We-Work';
import CoreOfferings from './components/Core-Offering-Home';
import LatestNews from './components/Latest-News';
import UpcomingEvents from './components/Upcoming-Events';

const HomePage = () => {
  return (
    <div>
      <Navbar />
      <div style={styles.container}>
        {/* Background Image */}
        <div style={styles.backgroundImage}>
          <img 
            src={HomePageBackground}
            alt="AI Neural Network Brain" 
            style={styles.bgImage}
          />
          <div style={styles.gradientOverlay}></div>
        </div>
        
        {/* Content Container */}
        <div style={styles.content}>
        {/* Badge */}
        <div style={styles.badge}>
          <Zap style={styles.badgeIcon} />
          <span style={styles.badgeText}>Advancing AI Education Globally</span>
        </div>

        {/* Main Heading */}
        <h1 style={styles.heading}>
          <span style={styles.headingNormal}>Empowering the future</span>
          <br />
          <span style={styles.headingNormal}>with </span>
          <span style={styles.headingGradient}>AI Education</span>
        </h1>

        {/* Subtitle */}
        <p style={styles.subtitle}>
          Join our global community of learners, researchers, and innovators 
          shaping the future of artificial intelligence through comprehensive 
          education and collaboration.
        </p>

        {/* CTA Buttons */}
        <div style={styles.buttonContainer}>
          <button style={styles.primaryButton}>
            <span>Start Learning</span>
            <ArrowRight style={styles.buttonIcon} />
          </button>
          <button style={styles.secondaryButton}>
            Explore Courses
          </button>
        </div>

        {/* Stats */}
        <div style={styles.statsContainer}>
          <div style={styles.statItem}>
            <div style={styles.statIconContainer}>
              <BookOpen style={styles.statIcon} />
            </div>
            <div style={styles.statNumber}>50+</div>
            <div style={styles.statLabel}>Multilingual AI Courses</div>
          </div>
          <div style={styles.statItem}>
            <div style={styles.statIconContainer}>
              <Users style={styles.statIcon} />
            </div>
            <div style={styles.statNumber}>1L+</div>
            <div style={styles.statLabel}>Global Learners</div>
          </div>
          <div style={styles.statItem}>
            <div style={styles.statIconContainer}>
              <Zap style={styles.statIcon} />
            </div>
            <div style={styles.statNumber}>100+</div>
            <div style={styles.statLabel}>Research Projects</div>
          </div>
        </div>
        </div>
      </div>
      <HomeAbout />
      <AITrainerKitSection />
      <AIFoundationLayout />
      <HowWeWorkSection />
      <CoreOfferings />
      <LatestNews />
      <UpcomingEvents />
      <AIRevolutionCTA />
      <Footer />
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: '8em 1em',
    position: 'relative',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    overflow: 'hidden',
  },
  backgroundImage: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 1,
  },
  bgImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    opacity: 0.6,
  },
  gradientOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'linear-gradient(135deg, #354aa6ff 0%, #6b86e9ff 100%)',
    opacity: 0.8,
  },
  content: {
    position: 'relative',
    zIndex: 10,
    width: '100%',
    maxWidth: '1200px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingTop: '2em',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '8px 16px',
    borderRadius: '50px',
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    marginBottom: '32px',
    backdropFilter: 'blur(10px)',
  },
  badgeIcon: {
    width: '16px',
    height: '16px',
    color: '#fbbf24',
    marginRight: '8px',
  },
  badgeText: {
    fontSize: '14px',
    fontWeight: '500',
    color: 'rgba(255, 255, 255, 0.9)',
  },
  heading: {
    fontSize: 'clamp(2.5rem, 8vw, 4.5rem)',
    fontWeight: '700',
    lineHeight: '1.1',
    marginBottom: '24px',
    maxWidth: '900px',
  },
  headingNormal: {
    color: 'white',
  },
  headingGradient: {
    background: 'linear-gradient(45deg, #eec612ff, #e37120ff)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  subtitle: {
    fontSize: 'clamp(1.1rem, 3vw, 1.5rem)',
    color: 'rgba(255, 255, 255, 0.85)',
    lineHeight: '1.6',
    marginBottom: '48px',
    maxWidth: '700px',
    margin: '0 auto 48px auto',
  },
  buttonContainer: {
    display: 'flex',
    gap: '16px',
    marginBottom: '80px',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  primaryButton: {
    display: 'flex',
    alignItems: 'center',
    padding: '14px 28px',
    fontSize: '16px',
    fontWeight: '600',
    color: 'white',
    background: 'linear-gradient(45deg, #eec612ff, #e37120ff)',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 15px rgba(59, 130, 246, 0.4)',
    cursor: 'default',
  },
  secondaryButton: {
    padding: '14px 28px',
    fontSize: '16px',
    fontWeight: '600',
    color: 'white',
    background: 'rgba(255, 255, 255, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    backdropFilter: 'blur(10px)',
    cursor: 'default',
  },
  buttonIcon: {
    width: '20px',
    height: '20px',
    marginLeft: '8px',
    transition: 'transform 0.3s ease',
  },
  statsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '48px',
    maxWidth: '600px',
    width: '100%',
  },
  statItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  statIconContainer: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '12px',
    backdropFilter: 'blur(10px)',
  },
  statIcon: {
    width: '24px',
    height: '24px',
    color: 'white',
  },
  statNumber: {
    fontSize: '2rem',
    fontWeight: '700',
    color: 'white',
    marginBottom: '4px',
  },
  statLabel: {
    fontSize: '14px',
    color: 'rgba(255, 255, 255, 0.7)',
    fontWeight: '500',
  },
  scrollIndicator: {
    position: 'absolute',
    bottom: '32px',
    left: '50%',
    transform: 'translateX(-50%)',
    animation: 'bounce 2s infinite',
  },
  scrollMouse: {
    width: '24px',
    height: '40px',
    border: '2px solid rgba(255, 255, 255, 0.5)',
    borderRadius: '12px',
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
  },
  scrollWheel: {
    width: '4px',
    height: '12px',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    borderRadius: '2px',
    marginTop: '8px',
    animation: 'scroll 2s infinite',
  },
};

// Add keyframe animations
const styleSheet = document.createElement('style');
styleSheet.type = 'text/css';
styleSheet.innerText = `
  @keyframes bounce {
    0%, 20%, 53%, 80%, 100% {
      transform: translateX(-50%) translateY(0);
    }
    40%, 43% {
      transform: translateX(-50%) translateY(-10px);
    }
    70% {
      transform: translateX(-50%) translateY(-5px);
    }
    90% {
      transform: translateX(-50%) translateY(-2px);
    }
  }
  
  @keyframes scroll {
    0% {
      opacity: 0;
      transform: translateY(-10px);
    }
    50% {
      opacity: 1;
    }
    100% {
      opacity: 0;
      transform: translateY(10px);
    }
  }
  
  @media (max-width: 768px) {
    .stats-container {
      grid-template-columns: 1fr !important;
      gap: 32px !important;
    }
    .button-container {
      flex-direction: column !important;
      align-items: center !important;
    }
  }
`;
document.head.appendChild(styleSheet);

export default HomePage;